<template>
  <div>
    <div class="text-medium-emphasis text-sm mb-3">
      {{ t("notifications.sendEmailWhen") }}
    </div>
    <div class="pt-3" style="max-width: 400px">
      <v-skeleton-loader v-if="!notifications" type="heading@3"></v-skeleton-loader>
      <template v-else>
        <v-text-field
          v-model.number="notifications.balance.max"
          clearable
          type="number"
          :label="t('notifications.balanceAbove')"
        >
          <template #append-inner>{{ currencySymbol }}</template>
        </v-text-field>
        <v-text-field
          v-model.number="notifications.balance.min"
          clearable
          type="number"
          :label="t('notifications.balanceBelow')"
        >
          <template #append-inner>{{ currencySymbol }}</template>
        </v-text-field>
        <v-text-field
          v-model.number="notifications.transaction.max"
          clearable
          type="number"
          :label="t('notifications.transactionAbove')"
        >
          <template #append-inner>{{ currencySymbol }}</template>
        </v-text-field>
        <v-select
          :model-value="notifications.inactivity.enabled ? notifications.inactivity.days : 0"
          :items="inactivityItems"
          :label="t('notifications.inactivity')"
          @update:model-value="
            (days: number) =>
              (notifications.inactivity = days
                ? { enabled: true, days }
                : { ...notifications.inactivity, enabled: false })
          "
        ></v-select>
      </template>
    </div>

    <v-btn class="mt-6" :loading="loading" :disabled="loading" color="success" @click="save">
      {{ t("common.save") }}
    </v-btn>
  </div>
</template>

<script lang="ts" setup>
import { useAccountCurrency } from "@/composables/useAccountCurrency";
import { useDocument } from "@/composables/useFirestore";
import { useI18n } from "@/composables/useI18n";
import { db } from "@/firebase";
import { useToast } from "@/store";
import type { Account } from "@aircap/common/account";
import { captureException } from "@sentry/vue";
import { doc, updateDoc } from "firebase/firestore";
import { computed, ref, toRef, watch } from "vue";

const props = defineProps<{
  accountId: string;
}>();

type notifs = {
  balance: {
    min: number | null;
    max: number | null;
  };
  transaction: {
    min?: number | null;
    max: number | null;
  };
  inactivity: {
    enabled: boolean;
    days: number;
  };
};

const { t } = useI18n();
const { showSuccessToast, showErrorToast } = useToast();
const { currencySymbol } = useAccountCurrency(toRef(props.accountId));

const docRef = doc<Account>(db, `accounts/${props.accountId}`);

const notifications = ref<notifs>({
  balance: {
    min: null,
    max: null,
  },
  transaction: {
    min: null,
    max: null,
  },
  inactivity: {
    enabled: false,
    days: 30,
  },
});

const inactivityItems = computed(() => [
  { title: t("notifications.inactivityNone"), value: 0 },
  ...[...new Set([30, 90, 180, notifications.value.inactivity.days])]
    .sort((a, b) => a - b)
    .map((days) => ({ title: t("notifications.inactivityDays", { days }), value: days })),
]);
const { data: account } = useDocument<Account>(docRef);
const loading = ref(false);

watch(
  account,
  (value) => {
    if (value) {
      notifications.value = {
        balance: { min: null, max: null },
        transaction: { min: null, max: null },
        ...value.notifications,
        inactivity: { enabled: false, days: 30, ...value.notifications?.inactivity },
      };
    }
  },
  { immediate: true },
);

const save = async () => {
  try {
    console.log("Saving notifications...");
    loading.value = true;
    await updateDoc(docRef, {
      notifications: notifications.value,
    });
    showSuccessToast(t("notifications.saved"));
  } catch (cause) {
    const error = new Error("Error saving notifications", { cause });
    console.error(error);
    captureException(error);
    showErrorToast();
  } finally {
    loading.value = false;
  }
};
</script>
